import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import SidePanel from '../../components/SidePanel/SidePanel';
import { apiList, shortDecimal } from '../../helpers';
import { groupDataByactivityTypeAndName } from '../../helpers/utils';
import ConstructionPlanTable from './ConstructionPlanTable';

// Side panel listing the construction plans this property had before it was
// reset (versions 1 … current-1): a summary card per version and, for the
// selected one, its activity table.

const DATE_FORMATS = ['DD-MM-YYYY HH:mm:ss', 'DD-MM-YYYY', moment.ISO_8601];

// Earliest createdAt of a version's rows = when that version was created.
const createdOn = (rows) => {
  const dates = rows
    .map((row) => moment(row.createdAt, DATE_FORMATS, true))
    .filter((d) => d.isValid());
  return dates.length ? moment.min(dates) : null;
};

const summarise = (rows) => {
  const activities = groupDataByactivityTypeAndName(rows);
  const count = (...statuses) => activities.filter((a) => statuses.includes(a.status)).length;
  const verified = rows
    .filter(({ status }) => status === 'Verified')
    .reduce((n, { weightage }) => n + (Number(weightage) || 0), 0);
  return {
    verifiedPct: shortDecimal(String(verified)),
    counts: [
      ['verified', count('Verified')],
      ['in progress', count('In Progress')],
      ['submitted', count('Submitted')],
      ['not started', count('Yet to start')],
    ].filter(([, n]) => n > 0),
    created: createdOn(rows),
  };
};

const useStyles = makeStyles({
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    margin: '8px 0 20px',
  },
  card: {
    display: 'block',
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #D7DEEA',
    borderRadius: 12,
    background: '#fff',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
    '&:hover': { borderColor: '#B9C3D6', background: '#FAFBFD' },
    '&:focus-visible': { outline: 'none', boxShadow: '0 0 0 3px rgba(69, 130, 255, 0.3)' },
  },
  selected: {
    borderColor: '#2E3B6E',
    background: '#F4F6FB',
    boxShadow: '0 0 0 1px #2E3B6E',
    '&:hover': { borderColor: '#2E3B6E', background: '#F4F6FB' },
  },
  cardTop: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
  },
  version: { fontSize: 15, fontWeight: 600, color: '#1F2733' },
  pct: {
    fontSize: 14, fontWeight: 600, color: '#067647', whiteSpace: 'nowrap',
  },
  meta: {
    marginTop: 6,
    fontSize: 13,
    lineHeight: 1.6,
    color: '#5B6472',
  },
  tableBox: {
    border: '1px solid #EAEEF5',
    borderRadius: 12,
    overflow: 'hidden',
  },
  tableTitle: {
    margin: '0 0 10px',
    fontSize: 13,
    fontWeight: 600,
    color: '#5B6472',
  },
  state: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
    fontSize: 14,
    color: '#8A94A6',
  },
});

export default function OldPlansPanel({
  open, onClose, propertyId, currentVersion, currentPlan, constructionPlanPermission,
}) {
  const classes = useStyles();
  const [versions, setVersions] = useState([]); // [{ version, rows }], newest first
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const resets = Math.max(0, (Number(currentVersion) || 1) - 1);

  useEffect(() => {
    if (!open || !propertyId || resets < 1) return undefined;
    let active = true;
    setLoading(true);
    const numbers = Array.from({ length: resets }, (_, i) => resets - i); // newest first
    Promise.all(numbers.map((v) => axios
      .get(`${apiList.VIEW_CONSTRUCTION_PLAN_API}/${propertyId}/${v}`)
      .then((response) => ({ version: v, rows: response?.data?.result?.constructionPlan || [] }))
      .catch(() => ({ version: v, rows: [] }))))
      .then((list) => {
        if (!active) return;
        setVersions(list);
        setSelected(list.length ? list[0].version : null);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, propertyId, resets]);

  // A version was replaced when the next one was created.
  const replacedOn = (version) => {
    if (version === resets) return createdOn(currentPlan || []);
    const next = versions.find((v) => v.version === version + 1);
    return next ? createdOn(next.rows) : null;
  };
  const fmt = (d) => (d ? d.format('DD-MM-YYYY') : null);
  const selectedRows = versions.find((v) => v.version === selected)?.rows || [];

  let body;
  if (loading) {
    body = <div className={classes.state}><CircularProgress size={28} /></div>;
  } else if (!versions.length) {
    body = <div className={classes.state}>No old plans found.</div>;
  } else {
    body = (
      <>
        <div className={classes.cards}>
          {versions.map(({ version, rows }) => {
            const s = summarise(rows);
            const created = fmt(s.created);
            const replaced = fmt(replacedOn(version));
            return (
              <button
                key={version}
                type='button'
                aria-pressed={selected === version}
                className={`${classes.card} ${selected === version ? classes.selected : ''}`}
                onClick={() => setSelected(version)}
              >
                <div className={classes.cardTop}>
                  <span className={classes.version}>{`Version ${version}`}</span>
                  <span className={classes.pct}>{`${s.verifiedPct}% verified`}</span>
                </div>
                <div className={classes.meta}>
                  {s.counts.length
                    ? s.counts.map(([label, n]) => `${n} ${label}`).join(' · ')
                    : 'No activities'}
                  {created || replaced ? <br /> : null}
                  {[created && `Created ${created}`, replaced && `Replaced ${replaced}`]
                    .filter(Boolean).join(' · ')}
                </div>
              </button>
            );
          })}
        </div>
        {selectedRows.length ? (
          <>
            <p className={classes.tableTitle}>{`Version ${selected} activities`}</p>
            <div className={classes.tableBox}>
              <ConstructionPlanTable
                bare
                isCurrentPlan={false}
                propertyId={propertyId}
                constructionPlanPermission={constructionPlanPermission}
                data={selectedRows}
              />
            </div>
          </>
        ) : null}
      </>
    );
  }

  return (
    <SidePanel
      open={open}
      setOpen={(value) => { if (!value) onClose(); }}
      title='Old plans'
      subtitle={`This plan has been reset ${resets} ${resets === 1 ? 'time' : 'times'}.`}
      showAction={false}
      width={800}
      body={body}
    />
  );
}
